import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveAssetUrl, trackEvent } from "@/lib/api";
import { CATEGORY_META } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft, ExternalLink, Lock, Copy, Check, ChevronRight,
  AlertTriangle, Sparkles, Workflow, ListChecks, Cpu,
} from "lucide-react";
import Lightbox from "@/components/Lightbox";
import ContactRequestDialog from "@/components/ContactRequestDialog";
import { toast } from "sonner";

const statusStyle = {
  live: "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30",
  beta: "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30",
  "coming-soon": "bg-blue-500/15 text-blue-300 ring-1 ring-blue-500/30",
  maintenance: "bg-slate-500/15 text-slate-300 ring-1 ring-slate-500/30",
};

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        const copyFallback = (text) => {
          const ta = document.createElement("textarea");
          ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
          document.body.appendChild(ta); ta.select();
          try { document.execCommand("copy"); } catch {}
          document.body.removeChild(ta);
        };
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
          } else {
            copyFallback(value);
          }
          setCopied(true);
          toast.success("Copied to clipboard");
          setTimeout(() => setCopied(false), 1400);
        } catch {
          copyFallback(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        }
      }}
      className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition-colors px-2 py-1 rounded-md hover:bg-slate-100"
      data-testid={testid}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function SystemDetail() {
  const { slug } = useParams();
  const [sys, setSys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [contactType, setContactType] = useState(null);

  useEffect(() => {
    setLoading(true);
    setUnlocked(false);
    api.get(`/systems/${slug}`)
      .then((r) => {
        setSys(r.data);
        trackEvent("page_view", { path: `/systems/${slug}`, system_id: r.data.id });
      })
      .catch(() => setSys(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const grouped = useMemo(() => {
    const out = {};
    credentials.forEach((c) => {
      (out[c.role] ||= []).push(c);
    });
    return out;
  }, [credentials]);

  const unlockDemo = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/leads", {
        email,
        request_type: "demo_unlock",
        system_id: sys.id,
        system_name: sys.name,
      });
      trackEvent("demo_unlock_click", { system_id: sys.id });
      const { data } = await api.get(`/systems/${slug}/credentials`);
      setCredentials(data);
      setUnlocked(true);
      toast.success("Demo unlocked — credentials revealed below.");
    } catch {
      toast.error("Could not unlock. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="pt-32 max-w-4xl mx-auto px-6 text-slate-500" data-testid="detail-loading">Loading…</div>;
  }
  if (!sys) {
    return (
      <div className="pt-32 max-w-4xl mx-auto px-6 text-center" data-testid="detail-not-found">
        <h1 className="font-heading text-3xl">System not found</h1>
        <Link to="/" className="text-blue-600 mt-4 inline-block">← Back to showroom</Link>
      </div>
    );
  }

  const meta = CATEGORY_META[sys.category];
  const Icon = meta?.icon;

  return (
    <div className="pt-16">
      {/* HEADER */}
      <section className="bg-slate-950 text-white relative overflow-hidden" data-testid="system-header">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white" data-testid="back-to-showroom">
            <ArrowLeft className="w-4 h-4" /> Back to showroom
          </Link>
          <div className="mt-8 grid lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
                {Icon ? <Icon className="w-3.5 h-3.5 text-[#D4AF37]" /> : null}
                <span>{sys.category}</span>
                <span className="w-1 h-1 rounded-full bg-slate-600" />
                <Badge className={`${statusStyle[sys.status] || statusStyle.live} rounded-full text-[10px] uppercase border-0`} data-testid="system-status">
                  {sys.status}
                </Badge>
              </div>
              <h1 className="mt-4 font-heading text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.05]" data-testid="system-name">
                {sys.name}
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed" data-testid="system-summary">
                {sys.short_summary}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button
                  asChild
                  className="rounded-full bg-[#D4AF37] text-slate-900 hover:bg-[#F3CE52] gold-glow font-semibold h-11 px-6"
                  data-testid="header-unlock-cta"
                >
                  <a href="#demo">Unlock Demo Access</a>
                </Button>
                <Button
                  onClick={() => setContactType("request_customization")}
                  variant="outline"
                  className="rounded-full h-11 px-6 bg-white/5 text-white hover:bg-white/10 hover:text-white border-white/20"
                  data-testid="header-customize-cta"
                >
                  Request Customization
                </Button>
              </div>
            </div>
            <div className="lg:col-span-1">
              {sys.thumbnail_url && (
                <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                  <img src={resolveAssetUrl(sys.thumbnail_url)} alt={sys.name} className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-16">

          {/* POSITIONING */}
          <section data-testid="section-positioning">
            <SectionTitle eyebrow="Positioning" title="Built for a clear problem" />
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="p-6 rounded-2xl border-slate-200">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Problem</div>
                <p className="mt-3 text-slate-700 leading-relaxed">{sys.problem}</p>
              </Card>
              <Card className="p-6 rounded-2xl border-slate-200">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Target Users</div>
                <ul className="mt-3 space-y-1.5">
                  {sys.target_users?.map((u, i) => (
                    <li key={i} className="text-slate-700 text-sm flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-[#D4AF37]" /> {u}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </section>

          {/* WHY */}
          <section data-testid="section-why">
            <SectionTitle eyebrow="Why this matters" title="Outcomes that move the business" />
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { l: "Revenue Impact", v: sys.why_revenue_impact, color: "text-emerald-600" },
                { l: "Cost Savings", v: sys.why_cost_savings, color: "text-blue-600" },
                { l: "Operational Edge", v: sys.why_operational_advantage, color: "text-[#D4AF37]" },
              ].map((b) => (
                <Card key={b.l} className="p-6 rounded-2xl border-slate-200">
                  <div className={`text-xs uppercase tracking-[0.18em] font-semibold ${b.color}`}>{b.l}</div>
                  <p className="mt-3 text-slate-700 leading-relaxed text-sm">{b.v}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* FEATURES */}
          <section data-testid="section-features">
            <SectionTitle eyebrow="Features" title="Everything teams need on day one" Icon={ListChecks} />
            <Tabs defaultValue="core" className="w-full">
              <TabsList className="bg-slate-100 rounded-full p-1">
                <TabsTrigger value="core" className="rounded-full" data-testid="tab-core">Core</TabsTrigger>
                <TabsTrigger value="advanced" className="rounded-full" data-testid="tab-advanced">Advanced</TabsTrigger>
                <TabsTrigger value="diff" className="rounded-full" data-testid="tab-differentiators">Differentiators</TabsTrigger>
              </TabsList>
              <TabsContent value="core" className="mt-6"><FeatureList items={sys.core_features} accent="gold" /></TabsContent>
              <TabsContent value="advanced" className="mt-6"><FeatureList items={sys.advanced_features} accent="blue" /></TabsContent>
              <TabsContent value="diff" className="mt-6"><FeatureList items={sys.differentiators} accent="gold" /></TabsContent>
            </Tabs>
          </section>

          {/* WORKFLOW */}
          <section data-testid="section-workflow">
            <SectionTitle eyebrow="Workflow" title="How it works" Icon={Workflow} />
            <ol className="space-y-4">
              {sys.workflow_steps?.map((step, i) => (
                <li key={i} className="flex gap-5 p-5 rounded-2xl border border-slate-200 bg-white">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-slate-900 text-[#D4AF37] grid place-items-center font-heading font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-slate-900">{step.title}</div>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* USE CASES */}
          <section data-testid="section-use-cases">
            <SectionTitle eyebrow="Use cases" title="Real outcomes from real teams" />
            <div className="grid md:grid-cols-2 gap-4">
              {sys.use_cases?.map((u, i) => (
                <Card key={i} className="p-6 rounded-2xl border-slate-200 bg-gradient-to-br from-white to-slate-50">
                  <div className="font-heading font-semibold text-slate-900">{u.title}</div>
                  <p className="text-slate-600 text-sm mt-2 leading-relaxed">{u.description}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* DEMO ACCESS */}
          <section id="demo" data-testid="section-demo">
            <SectionTitle eyebrow="Demo Access" title="Try it now" />
            {!unlocked ? (
              <Card className="p-8 rounded-2xl border-slate-200 bg-gradient-to-br from-slate-50 to-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-900 text-[#D4AF37] grid place-items-center shrink-0">
                    <Lock className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading text-xl font-semibold">Unlock demo credentials</div>
                    <p className="text-slate-600 text-sm mt-1">Drop your work email to instantly reveal sandbox credentials and the live demo link.</p>
                    <form onSubmit={unlockDemo} className="mt-5 flex flex-col sm:flex-row gap-3">
                      <Input
                        type="email"
                        required
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12 rounded-full bg-white border-slate-300"
                        data-testid="demo-unlock-email"
                      />
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="h-12 rounded-full bg-slate-900 hover:bg-slate-800 px-6"
                        data-testid="demo-unlock-submit-button"
                      >
                        {submitting ? "Unlocking…" : "Unlock Access"}
                      </Button>
                    </form>
                    <div className="text-[11px] text-slate-500 mt-3 uppercase tracking-wider">No spam. We use your email to share demo details and follow-up.</div>
                  </div>
                </div>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="demo-unlocked-block">
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-700" />
                  <AlertDescription className="text-amber-800 text-xs uppercase tracking-wider font-semibold">
                    For evaluation purposes only — sandbox credentials, not for production.
                  </AlertDescription>
                </Alert>

                <Card className="p-6 rounded-2xl border-slate-200 bg-slate-950 text-white">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Demo URL</div>
                      <div className="font-mono text-sm mt-1 break-all" data-testid="demo-url">{sys.demo_url || "—"}</div>
                    </div>
                    <Button
                      asChild
                      className="rounded-full bg-[#D4AF37] text-slate-900 hover:bg-[#F3CE52] font-semibold"
                      data-testid="launch-demo-button"
                    >
                      <a href={sys.demo_url || "#"} target="_blank" rel="noopener noreferrer">
                        Launch Demo <ExternalLink className="w-4 h-4 ml-1.5" />
                      </a>
                    </Button>
                  </div>
                </Card>

                {Object.entries(grouped).map(([role, creds]) => (
                  <Card key={role} className="p-6 rounded-2xl border-slate-200" data-testid={`credential-group-${role.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{role}</div>
                    <div className="mt-4 space-y-4">
                      {creds.map((c) => (
                        <div key={c.id} className="grid sm:grid-cols-2 gap-4">
                          <CredField label="Username" value={c.username} testid={`cred-username-${c.id}`} />
                          <CredField label="Password" value={c.password} testid={`cred-password-${c.id}`} mono />
                          {c.notes && (
                            <div className="sm:col-span-2 text-xs text-slate-500 leading-relaxed bg-slate-50 rounded-lg p-3">
                              <span className="font-semibold uppercase tracking-wider text-slate-600">Notes: </span>{c.notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {/* SCREENSHOTS */}
          {sys.screenshots?.length > 0 && (
            <section data-testid="section-screenshots">
              <SectionTitle eyebrow="Screenshots" title="A look inside" />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sys.screenshots.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxIdx(i)}
                    className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100"
                    data-testid={`screenshot-${i}`}
                  >
                    <img src={resolveAssetUrl(src)} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
          <Card className="p-6 rounded-2xl border-slate-200" data-testid="sidebar-tech-stack">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5 text-[#2563EB]" /> Tech Stack
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {sys.tech_stack?.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium font-mono">{t}</span>
              ))}
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border-slate-200 bg-slate-950 text-white">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-300 font-semibold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" /> Talk to us
            </div>
            <h3 className="mt-3 font-heading text-xl">Need it tailored to your business?</h3>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">Our solution engineers can customize features, integrations, and deployment to your stack.</p>
            <div className="mt-5 flex flex-col gap-2">
              <Button
                onClick={() => setContactType("schedule_demo")}
                className="rounded-full bg-[#D4AF37] text-slate-900 hover:bg-[#F3CE52] font-semibold"
                data-testid="sidebar-schedule-demo"
              >
                Schedule Demo
              </Button>
              <Button
                onClick={() => setContactType("request_customization")}
                variant="outline"
                className="rounded-full bg-white/5 hover:bg-white/10 text-white hover:text-white border-white/20"
                data-testid="sidebar-request-customization"
              >
                Request Customization <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </Card>
        </aside>
      </div>

      <Lightbox images={sys.screenshots || []} openIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      <ContactRequestDialog
        open={contactType !== null}
        onOpenChange={(o) => !o && setContactType(null)}
        requestType={contactType || "schedule_demo"}
        system={sys}
      />
    </div>
  );
}

function SectionTitle({ eyebrow, title, Icon }) {
  return (
    <div className="mb-6">
      <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-2">
        {Icon ? <Icon className="w-3.5 h-3.5 text-[#D4AF37]" /> : null}
        {eyebrow}
      </div>
      <h2 className="font-heading text-2xl sm:text-3xl font-semibold mt-2 tracking-tight">{title}</h2>
    </div>
  );
}

function FeatureList({ items, accent }) {
  if (!items || items.length === 0) return <div className="text-slate-500 text-sm">No items listed.</div>;
  const dot = accent === "gold" ? "bg-[#D4AF37]" : "bg-[#2563EB]";
  return (
    <ul className="grid sm:grid-cols-2 gap-3">
      {items.map((f, i) => (
        <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-200">
          <span className={`mt-1.5 w-2 h-2 rounded-full ${dot}`} />
          <span className="text-slate-700 text-sm leading-relaxed">{f}</span>
        </li>
      ))}
    </ul>
  );
}

function CredField({ label, value, mono, testid }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
        <span className={`${mono ? "font-mono" : ""} text-sm text-slate-900 break-all`} data-testid={testid}>{value}</span>
        <CopyButton value={value} testid={`${testid}-copy`} />
      </div>
    </div>
  );
}
